<template>
  <div class="mb-3">
    <DataTable ref="dt" v-model:selection="localSelected" :value="loading ? skeleton : items"
      :selectionMode="loading || !items.length ? null : 'single'" dataKey="id" :metaKeySelection="false" scrollable
      tableStyle="min-width: 50rem" :rowClass="rowClass" @row-unselect="e => localSelected = e.data">
      <template #empty>
        <div style="text-align: center; padding: 20px 0;">
          <img src="/no_data.png" alt="No Data" style="max-width: 150px; opacity: 0.7; margin-bottom: 0;" />
          <div style="color: #888; font-size: 1.1em; font-weight: 500;">No Data</div>
        </div>
      </template>
      <Column v-for="(col, index) in columns" :field="col.field" :key="col.field + '_' + index" :header="col.header"
        :style="col.style" :sortable="col.type !== 'note-button'">
        <template #body="{ data }">
          <Skeleton v-if="loading" width="100%" height="1.5rem" />
          <div v-else-if="col.type === 'note-button'" style="display: inline-flex; align-items: center; gap: 4px;">
            <ButtonPrime
              v-tooltip.bottom="'MCR Item Note'"
              icon="pi pi-file-edit"
              rounded
              outlined
              size="small"
              :disabled="!selected || data.id !== selected.id"
              @click.stop="$emit('note-click', data)"
            />
            <ButtonPrime
              v-if="checkDuplicateVisible(data)"
              v-tooltip.bottom="'Check Duplicate'"
              icon="pi pi-copy"
              rounded
              outlined
              severity="warning"
              size="small"
              :disabled="!selected || data.id !== selected.id"
              @click.stop="$emit('check-duplicate-click', data)"
            />
            <ButtonPrime
              v-if="cancelVisible(data)"
              v-tooltip.bottom="'Cancel'"
              icon="pi pi-ban"
              rounded
              outlined
              severity="danger"
              size="small"
              :disabled="!selected || data.id !== selected.id"
              @click.stop="$emit('cancel-click', data)"
            />
            <span v-if="data.status" v-tooltip.bottom="statusBadgeTooltip(data)" class="badge" :class="statusBadgeClass(data.status)" :style="statusBadgeStyle(data.status)" style="font-size: 0.6rem; padding: 2px 4px; min-width: 0;">{{ statusBadgeLabel(data.status) }}</span>
          </div>
          <span v-else style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;">{{
            col.getter ? col.getter(data) : getByPath(data, col.field) }}</span>
        </template>
      </Column>
      <Column v-if="showActions" header="Action" header-class="" body-style="text-align: center; width: 80px;">
        <template #body="{ data }">
          <Skeleton v-if="loading" width="100%" height="1.5rem" />
          <div v-else>
            <ButtonPrime v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded severity="danger" size="small"
              @click.stop="$emit('delete', data)" />
          </div>
        </template>
      </Column>
      <template v-if="loadingMore" #footer>
        <div class="text-center py-2 text-secondary">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading more...
        </div>
      </template>
    </DataTable>
  </div>
</template>
<script>
export default {
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => ({ current_page: 1, per_page: 10, total_page: 1 }),
    },
    total: {
      type: Number,
      default: 0,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Object,
      default: null,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    checkDuplicateVisible: {
      type: Function,
      default: () => false,
    },
    cancelVisible: {
      type: Function,
      default: () => true,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:selected', 'page-change', 'update:pagination', 'delete', 'note-click', 'check-duplicate-click', 'cancel-click', 'load-more'],
  computed: {
    localCurrentPage: {
      get() {
        return this.pagination.current_page;
      },
      set(val) {
        this.$emit('update:pagination', { ...this.pagination, current_page: val });
      },
    },
    localSelected: {
      get() {
        return this.selected;
      },
      set(val) {
        this.$emit('update:selected', val);
      },
    },
  },
  methods: {
    rowClass(rowData) {
      if (!this.selected || !this.selected.id || !rowData.id) return '';
      return this.selected.id === rowData.id ? 'selected-row' : '';
    },
    getByPath(obj, path) {
      return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    },
    statusBadgeClass(status) {
      const map = {
        sap: 'bg-success',
        catalogued: 'bg-primary',
        canceled: 'bg-danger',
        error: 'bg-danger',
        draft: 'bg-secondary',
      };
      if (map[status]) return map[status];
      return String(status || '').startsWith('duplicated') ? '' : 'bg-secondary';
    },
    // 'purple' bukan bagian dari $theme-colors Argon (cuma di $colors), jadi ga ada kelas bg-purple siap pakai — pakai inline style
    statusBadgeStyle(status) {
      return String(status || '').startsWith('duplicated') ? { backgroundColor: '#6f42c1' } : {};
    },
    statusBadgeLabel(status) {
      const map = {
        sap: 'SAP',
        catalogued: 'CAT',
        canceled: 'CANC',
        error: 'ERR',
        draft: 'DRF',
      };
      if (map[status]) return map[status];
      return String(status || '').startsWith('duplicated') ? 'DUPL' : status;
    },
    // Tooltip cuma muncul untuk status catalogued/canceled/duplicated, dan cuma kalau data pelakunya ada.
    statusBadgeTooltip(data) {
      const status = data?.status;
      const by = data?.catalogued_by || {};
      if (status === 'catalogued' && by.catalogued_by?.name) {
        return `Catalogued By : ${by.catalogued_by.name}`;
      }
      if (status === 'canceled' && by.canceled_by?.name) {
        return `Canceled By : ${by.canceled_by.name}`;
      }
      if (String(status || '').startsWith('duplicated') && by.duplicated_by?.name) {
        return `Duplicated By : ${by.duplicated_by.name}`;
      }
      return null;
    },
    onScroll(e) {
      const el = e.target;
      const threshold = 80;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
        this.$emit('load-more');
      }
    },
  },
  mounted() {
    this.scrollEl = this.$refs.dt?.$el?.querySelector('.p-datatable-table-container');
    this.scrollEl?.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    this.scrollEl?.removeEventListener('scroll', this.onScroll);
  },
};
</script>
<style scoped>
:deep(.p-datatable-table-container) {
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
}

:deep(.p-datatable-thead > tr > th) {
  background-color: #185ea2 !important;
  color: #ffffff !important;
}

:deep(.p-datatable-thead > tr > th .p-column-header-content) {
  color: #ffffff !important;
}

:deep(.p-datatable-thead > tr > th .p-column-title) {
  color: #ffffff !important;
}

:deep(.p-datatable-thead > tr > th .p-sortable-column-icon) {
  color: #ffffff !important;
}

:deep(.p-datatable-thead > tr > th svg) {
  color: #ffffff !important;
  fill: #ffffff !important;
}

:deep(.p-datatable-thead > tr > th .p-icon) {
  color: #ffffff !important;
  fill: #ffffff !important;
}
</style>
